/**
 * RTSP + motion advice (ENG-2828, admin PR #3274).
 *
 * Motion signals start a pull from the camera, and on a site that streams
 * over RTSP that pull connects slower than HTTP streaming, so motion works
 * but loses the first moments. Advisory only: it never blocks a save.
 *
 * The site About payload (`GET/PATCH customer/{id}/about/`) carries
 * `motion_streaming_advice`: the recommendation text when the site's stream
 * `protocol` is rtsp and the site's `use_motion` is on, null otherwise. A
 * camera's "Use motion" override is ignored while the site's use_motion is
 * off (admin `Camera.effective_motion_override`), so it never counts. Always show the server's
 * string; the constant below is only the fallback for the camera page, where
 * the operator is choosing "Use motion" before the server has seen it.
 */

export const MOTION_STREAMING_ADVICE_FIELD = "motion_streaming_advice";

export const MOTION_RTSP_ADVICE_FALLBACK =
  "Motion is on while this site streams over RTSP. We recommend switching streaming to HTTP: it connects faster when motion starts a pull.";

/** The server's advice when it is a non-empty string; otherwise null. */
export const siteMotionStreamingAdvice = (
  siteInfo: Record<string, any> | null | undefined
): string | null => {
  const advice = siteInfo?.[MOTION_STREAMING_ADVICE_FIELD];
  return typeof advice === "string" && advice.trim() !== "" ? advice : null;
};

export const isRtspProtocol = (protocol: unknown): boolean =>
  typeof protocol === "string" && protocol.trim().toLowerCase() === "rtsp";

/**
 * Advice to show under a camera's Motion setting: only while "Use motion" is
 * chosen. Prefers the server's string; otherwise falls back to the fixed text
 * when the site has motion on AND streams over RTSP. Never on protocol alone:
 * with the site's use_motion off, admin ignores the camera's override, so
 * motion is not actually on.
 */
export const cameraMotionStreamingAdvice = (
  siteInfo: Record<string, any> | null | undefined,
  usesMotionOverride: boolean
): string | null => {
  if (!usesMotionOverride) return null;
  return (
    siteMotionStreamingAdvice(siteInfo) ??
    (siteInfo?.use_motion === true && isRtspProtocol(siteInfo?.protocol)
      ? MOTION_RTSP_ADVICE_FALLBACK
      : null)
  );
};

/**
 * Site info after a successful About PATCH: the advice depends on the saved
 * protocol / use_motion, so take it from the response when the response
 * carries it. Unchanged when it doesn't (an older admin).
 */
export const withSavedMotionStreamingAdvice = <T extends Record<string, any>>(
  siteInfo: T,
  responseData: unknown
): T => {
  if (
    !responseData ||
    typeof responseData !== "object" ||
    !Object.prototype.hasOwnProperty.call(
      responseData,
      MOTION_STREAMING_ADVICE_FIELD
    )
  ) {
    return siteInfo;
  }
  return {
    ...siteInfo,
    [MOTION_STREAMING_ADVICE_FIELD]: (responseData as Record<string, any>)[
      MOTION_STREAMING_ADVICE_FIELD
    ],
  };
};
